// src/pages/super-admin/Clients/ClientUsersSection.tsx
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useGetClientUsersQuery } from "@/api/clientApi";
import { Badge } from "@/components/ui/badge";
import CreateClientUser from "./CreateClientUser";
import EditClientUser from "./EditClientUser";
import { useState } from "react";
import { User, Phone, Mail, Edit3 } from "lucide-react";

interface ClientUsersSectionProps {
  clientId: number;
}

export default function ClientUsersSection({
  clientId,
}: ClientUsersSectionProps) {
  const { data = [], isLoading, refetch } = useGetClientUsersQuery(clientId);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<any>(null);

  const handleUserCreated = () => {
    setDialogOpen(false);
    refetch();
  };

  const handleEditUser = (emp: any) => {
    setSelectedEmployee(emp);
    setEditDialogOpen(true);
  };

  const handleUserUpdated = () => {
    setEditDialogOpen(false);
    setSelectedEmployee(null);
    refetch();
  };

  return (
    <Card>
      <CardHeader className="flex justify-between items-center">
        <CardTitle>Employees ({data.length})</CardTitle>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button>+ Add Employee</Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Create New Employee</DialogTitle>
            </DialogHeader>
            <CreateClientUser
              clientId={clientId}
              onSuccess={handleUserCreated}
            />
          </DialogContent>
        </Dialog>
      </CardHeader>

      <CardContent>
        {isLoading ? (
          <div className="text-center py-8 text-gray-500">
            Loading employees...
          </div>
        ) : data.length === 0 ? (
          <div className="text-center py-8 space-y-4">
            <p className="text-sm text-gray-500">No employees found</p>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button>Add First Employee</Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Create New Employee</DialogTitle>
                </DialogHeader>
                <CreateClientUser
                  clientId={clientId}
                  onSuccess={handleUserCreated}
                />
              </DialogContent>
            </Dialog>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3">
            {data.map((emp: any) => (
              <div
                key={emp.id}
                className="p-4 border rounded-xl hover:shadow-md transition-all flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white"
              >
                <div className="flex gap-4 items-center min-w-0 flex-1">
                  <div className="h-12 w-12 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-600 font-bold uppercase shrink-0">
                    {emp.firstName?.[0]}{emp.lastName?.[0]}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className="font-bold text-slate-900 text-base leading-tight">
                      {emp.firstName} {emp.lastName}
                    </h4>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-x-4 gap-y-1 mt-1.5">
                      <div className="flex items-center gap-1.5">
                        <Mail className="w-3.5 h-3.5 text-blue-500" />
                        <span className="text-xs text-slate-600 font-semibold color-blue-500">{emp.email}</span>
                      </div>
                      {(emp.contactPhone || emp.phoneNumber) && (
                        <div className="flex items-center gap-1.5">
                          <Phone className="w-3.5 h-3.5 text-blue-500" />
                          <span className="text-xs text-slate-600 font-semibold">
                            {emp.contactPhone || emp.phoneNumber}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-4 w-full sm:w-auto">
                  <div className="flex flex-col items-start sm:items-end">
                    <span className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Role</span>
                    <div className="flex items-center gap-1.5">
                      <User className="w-3 h-3 text-slate-400" />
                      <span className="text-xs font-semibold text-slate-700">Client Employee</span>
                    </div>
                  </div>

                  <div className="flex flex-col items-start sm:items-end border-l pl-4 border-slate-100">
                    <span className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-0.5">Status</span>
                    <Badge
                      className={`font-bold border-0 h-6 px-2 ${emp.status === "ACTIVE"
                        ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100"
                        : "bg-slate-100 text-slate-600 hover:bg-slate-100"
                        }`}
                    >
                      {emp.status}
                    </Badge>
                  </div>

                  <div className="flex items-center gap-2 border-l pl-4 border-slate-100">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 gap-1.5"
                      onClick={() => handleEditUser(emp)}
                    >
                      <Edit3 className="w-3.5 h-3.5" />
                      <span className="text-xs font-bold">Edit</span>
                    </Button>
                  </div>
                </div>
              </div>
            ))}

            <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Edit Employee</DialogTitle>
                </DialogHeader>
                {selectedEmployee && (
                  <EditClientUser
                    clientId={clientId}
                    employee={selectedEmployee}
                    onSuccess={handleUserUpdated}
                  />
                )}
              </DialogContent>
            </Dialog>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
